import React from 'react';
import PropTypes from 'prop-types';
import { I18n as I } from 'react-redux-i18n';
import { history } from 'data/Store';

import AuctionBidModal from 'components/bids/AuctionBidModal';
import UpgradeMembershipModal from 'components/bids/UpgradeMembershipModal';

import {
  ConfirmationModal,
  formatCurrencyRounded,
  isArray,
  PartyTypes,
  Memberships,
  AuctionStatuses,
} from 'vdlib';

import Portlet from 'components/layout/Portlet';
import LookupsHelper from 'lib/LookupsHelper';

import RoleStatusColumnDefs from './ui-data/AuctionsColumnsDefs';

import AuctionDurationModal from './AuctionDurationModal';
import AuctionsListFilters from './AuctionsListFilters';

import ImageGallery from 'components/common/ImageGallery';
import { Spin, Table, Popconfirm, Button } from 'antd';

import { getColumns } from './columns';

const pagination = { position: 'bottom', size: 'small' };

const stepTitleOlder = 'Παλιότερες';
const stepTitleNewer = 'Νεότερες';

const stringComparator = (value, filterValue) => {
  if (!value) return false;
  const stringValue = String(value).toLowerCase();
  const compareValue = String(filterValue).toLowerCase();
  return stringValue.indexOf(compareValue) > -1;
};

const findBestOffer = bids =>
  bids
    .map(x => x.Amount)
    .reduce((currentMax, current) => Math.max(currentMax, current), 0);

const AuctionsList = ({
  acceptAuction,
  auctions,
  auctionStatus,
  bids,
  cancelAuction,
  cancelAuctions,
  cancelling,
  clearFilters,
  creatingAuctionBidsFor,
  createBid,
  createdBid,
  dispatchAuction,
  currentAuctionBids,
  endAuctions,
  exportToPdfCarAuctionPDF,
  fetchAuctions,
  fetchBidsForAuction,
  fetchingAuctions,
  filters,
  //history,
  insurances,
  lang,
  location,
  lost,
  memberships,
  noOffer,
  participate,
  partyTypes,
  regions,
  setFilter,
  startAuctions,
  submitting,
  user,
  vehicleTypes,
  merchantFees,
}) => {
  const [currentStep, setCurrentStep] = React.useState(0);
  const [modalType, setModalType] = React.useState(null);
  const [selection, setSelection] = React.useState({});
  const [galleryImages, setGalleryImages] = React.useState(null);
  const [goldMarketValue, setGoldMarketValue] = React.useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = React.useState(false);

  const [bidStep, setBidStep] = React.useState(0);
  const [best, setBest] = React.useState(0);
  const [current, setCurrent] = React.useState(0);

  const [auctionToDispatch, setAuctionToDispatch] = React.useState({});
  const [auctionToStartId, setAuctionToStartId] = React.useState(null);
  const [auctionToAccept, setAuctionToAccept] = React.useState({});
  const [auctionToCancel, setAuctionToCancel] = React.useState({});
  const [selectedAuction, setSelectedAuction] = React.useState({});

  const {
    isExpert,
    isInsurance,
    isMerchant,
    isOperator,
    partyType,
    columnDefsKey,
  } = React.useMemo(() => {
    if (!partyTypes || !user) return {};
    const partyType = LookupsHelper.getDescription(partyTypes, user.PartyType);
    return {
      isExpert: partyType === PartyTypes.Expert,
      isInsurance: partyType === PartyTypes.Insurance,
      isMerchant: partyType === PartyTypes.Merchant,
      isOperator:
        partyType === PartyTypes.Operator ||
        partyType == PartyTypes.Admin_Insurance,
      partyType,
      columnDefsKey: `${partyType}_${auctionStatus}`,
    };
  }, [auctionStatus, partyTypes, user]);

  /* Required callbacks for column defs */

  const onPhotoClick = React.useCallback(
    ({ FolderNumber, Images, Thumbnails }) =>
      setGalleryImages(
        Images.map((img, i) => ({
          id: img.Filepath,
          original: img.Filepath,
          thumbnail: Thumbnails[i].Filepath,
        }))
      ),
    [setGalleryImages]
  );

  const currentBid = bids => {
    if (!bids) return null;
    const filtered = bids.filter(x => x.BidderID === user.PartyId);
    return filtered.length > 0
      ? filtered.reduce(
          (max, p) => (p.Amount > max ? p.Amount : max),
          bids[0].Amount
        )
      : null;
  };

  const onOfferClick = React.useCallback(
    auction => {
      if (auction.OnlineMode !== true) fetchBidsForAuction(auction.ID);

      setModalType('offer-auction');
      setGoldMarketValue(auction.MarketValue >= 7000);
      setShowUpgradeModal(
        auction.MarketValue >= 7000 &&
          user.MembershipId === getMembershipId(Memberships.Bronze)
      );

      setBidStep(auction.BidStep);
      setBest(auction.BestOffer);
      setCurrent(currentBid(auction.Bids));
      setSelectedAuction(auction);
    },
    //eslint-disable-next-line
    [
      fetchBidsForAuction,
      setModalType,
      setGoldMarketValue,
      setSelectedAuction,
      setShowUpgradeModal,
    ]
  );

  /* Column Definitions */
  const columnDefsDataContext = React.useMemo(
    () => ({
      exportToPdfCarAuctionPDF,
      findBestOffer,
      insurances,
      onOfferClick,
      onPhotoClick,
      regions,
      setAuctionToAccept,
      user,
      columnDefsKey,
    }),
    [
      exportToPdfCarAuctionPDF,
      insurances,
      onOfferClick,
      onPhotoClick,
      regions,
      setAuctionToAccept,
      user,
      columnDefsKey,
    ]
  );

  const columnDefs = React.useMemo(() => {
    if (!insurances || !partyType) return null;
    const allColumnDefs = getColumns(columnDefsDataContext);

    let columnsSet = RoleStatusColumnDefs[columnDefsKey];

    if (!columnsSet && participate && partyType === PartyTypes.Merchant)
      columnsSet = RoleStatusColumnDefs[`${partyType}_Participate`];
    if (!columnsSet && lost && partyType === PartyTypes.Merchant)
      columnsSet = RoleStatusColumnDefs[`${partyType}_Lost`];
    if (
      !columnsSet &&
      noOffer &&
      (partyType === PartyTypes.Operator ||
        partyType == PartyTypes.Admin_Insurance)
    )
      columnsSet = RoleStatusColumnDefs[`${partyType}_NoOffer`];

    return columnsSet
      ? columnsSet.map(elem => allColumnDefs.find(cd => cd.key === elem))
      : console.error(
          `AuctionsList: Could not find suitable columns set for type: ${partyType}, status: ${auctionStatus}, participate: ${participate}`
        );
    // eslint-disable-next-line
  }, [
    auctionStatus,
    columnDefsDataContext,
    insurances,
    lang,
    lost,
    noOffer,
    participate,
    partyType,
  ]);

  // what the user *can* do
  const can = React.useMemo(() => {
    const obj = {
      acceptAuction:
        partyType === PartyTypes.Merchant &&
        auctionStatus === AuctionStatuses.ForAcceptance,
      cancel:
        auctions != null &&
        partyType === PartyTypes.Insurance &&
        auctionStatus === AuctionStatuses.ForSelection &&
        !!auctions.find(a => !isArray(a.Bids) || a.Bids.length === 0),
      cancelAuction:
        (partyType === PartyTypes.Insurance ||
          partyType === PartyTypes.Operator ||
          partyType === PartyTypes.Admin_Insurance) &&
        [
          AuctionStatuses.Active,
          AuctionStatuses.ForSelection,
          AuctionStatuses.Pending,
        ].includes(auctionStatus),
      endAuction:
        partyType === PartyTypes.Insurance &&
        [AuctionStatuses.Active, AuctionStatuses.ForSelection].includes(
          auctionStatus
        ),
      makeBid:
        partyType === PartyTypes.Merchant &&
        auctionStatus === AuctionStatuses.Active,
      startAuction:
        (partyType === PartyTypes.Operator ||
          partyType === PartyTypes.Admin_Insurance) &&
        auctionStatus === AuctionStatuses.Pending,
    };
    obj.cancelBid = obj.makeBid && Array.isArray(bids) && bids.length > 0;
    return obj;
  }, [auctions, bids, partyType, auctionStatus]);

  // brands to be used in dropdown
  const { brands } = React.useMemo(() => {
    const brandModels = {};

    if (auctions != null) {
      for (let d of auctions) {
        if (!isArray(brandModels[d.VehicleBrand]))
          brandModels[d.VehicleBrand] = [];
        if (brandModels[d.VehicleBrand].indexOf(d.VehicleModel) < 0)
          brandModels[d.VehicleBrand].push(d.VehicleModel);
      }
    }

    return {
      brands: Object.keys(brandModels)
        .sort()
        .map((b, i) => ({ ID: i + 1, Description: b })),
      brandModels,
    };
  }, [auctions]);

  const isLast = React.useMemo(
    () => auctions && auctions.length < 100,
    [auctions]
  );

  // actual auction data that will be sent to table
  const filteredAuctions = React.useMemo(() => {
    const filterFunc =
      Array.isArray(filters) && filters.length > 0
        ? auction =>
            filters
              .filter(x => x.Name !== 'forLocation')
              .every(f => {
                if (!f.Name) return false;
                return stringComparator(auction[f.Name], f.Value);
              })
        : item => item;

    return (auctions || []).filter(filterFunc);
  }, [auctions, filters]);

  const columnsWidth = React.useMemo(
    () =>
      (columnDefs || []).reduce(
        (sum, columnDef) =>
          sum + (typeof columnDef.width === 'number' ? columnDef.width : 0),
        300
      ),
    [columnDefs]
  );

  // Close offer modal if auction expired
  React.useEffect(() => {
    if (auctions && selectedAuction && modalType === 'offer-auction') {
      const auction = auctions.find(a => a.ID === selectedAuction.ID);
      if (!auction) setModalType(null);
    }
  }, [auctions, modalType, setModalType, selectedAuction]);

  /* Callbacks */

  const loadOlder = React.useCallback(
    () => setCurrentStep(currStep => currStep + 1),
    [setCurrentStep]
  );
  const loadNewer = React.useCallback(
    () => setCurrentStep(currStep => currStep - 1),
    [setCurrentStep]
  );

  const hideModals = React.useCallback(() => {
    setAuctionToAccept({});
    setAuctionToCancel({});
    setAuctionToDispatch({});
    setAuctionToStartId(null);
    setCurrent(0);

    setModalType(null);
  }, [
    setAuctionToAccept,
    setAuctionToCancel,
    setAuctionToDispatch,
    setAuctionToStartId,
    setModalType,
  ]);

  const submitEndAuctions = React.useCallback(() => {
    endAuctions(selection.selectedRowsAuctionIDs, filters.Bids);
    hideModals();
  }, [endAuctions, selection, filters, hideModals]);

  const submitStartAuctions = React.useCallback(
    endDate => startAuctions(selection.selectedRowsAuctions),
    [selection, startAuctions]
  );

  const submitCancelAuctions = React.useCallback(
    () => cancelAuctions(selection.selectedRowsAuctionIDs),
    [selection, cancelAuctions]
  );

  const onSelectChange = React.useCallback(
    (selectedRowKeys, selectedRows) =>
      setSelection({
        selectedRowKeys,
        selectedRowsTotal: selectedRows.length,
        selectedRowsAuctionIDs: Array.isArray(selectedRows)
          ? selectedRows.map(r => r.ID)
          : [],
        selectedRowsAuctions: Array.isArray(selectedRows) ? selectedRows : [],
      }),
    [setSelection]
  );

  const onBidSubmit = React.useCallback(
    (bidAmount, auction) => {
      createBid(
        auction.ID,
        [
          {
            Amount: bidAmount,
          },
        ],
        user.PartyId
      );
    },
    [createBid, user]
  );

  const getMembershipId = React.useCallback(
    membership => LookupsHelper.getIdByDescription(memberships, membership),
    [memberships]
  );

  const imageGalleryRef = React.useCallback(elem => {
    if (elem && elem.fullScreen) elem.fullScreen();
  }, []);

  React.useEffect(() => {
    if (Array.isArray(filters) && filters.length > 0) {
      const { Value: filterLocation } =
        filters.find(f => f.Name === 'forLocation') || {};
      if (!filterLocation || filterLocation !== location.pathname) {
        if (!!filterLocation) clearFilters();
        setFilter('forLocation', location.pathname);
      }
    }
  }, []); // eslint-disable-line

  React.useEffect(() => {
    fetchAuctions(currentStep);
  }, [fetchAuctions, currentStep]);

  // conditions under which to hide modals
  React.useEffect(() => {
    if (submitting || cancelling) hideModals();
  }, [hideModals, cancelling, submitting]);

  React.useEffect(() => {
    if (createdBid) history.push({ pathname: '/' });
  }, [createdBid]);

  const { selectedRowsAuctions, selectedRowsTotal, selectedRowKeys } =
    selection;

  const rowSelection = React.useMemo(
    () => ({
      fixed: true,
      onChange: onSelectChange,
      renderCell: (checked, record, index, originNode) => (
        <div className="cell-fix-wrap">{originNode}</div>
      ),
      selectedRowKeys,
    }),
    [onSelectChange, selectedRowKeys]
  );

  const { onlineFilteredAuctions, normalFilteredAuctions } = React.useMemo(
    () => ({
      onlineFilteredAuctions: filteredAuctions.filter(x => x.OnlineMode),
      normalFilteredAuctions: filteredAuctions.filter(x => !x.OnlineMode),
    }),
    [filteredAuctions]
  );
  const hasOnline = onlineFilteredAuctions.length > 0;

  return partyType ? (
    <div>
      <h1 className="page-title">{I.t('AUCTIONS')}</h1>
      <AuctionsListFilters
        brands={brands}
        filters={filters}
        insurances={insurances}
        isExpert={isExpert}
        isInsurance={isInsurance}
        isOperator={isOperator}
        lang={lang}
        location={location}
        regions={regions}
        setFilter={setFilter}
        vehicleTypes={vehicleTypes}
        columnDefsKey={columnDefsKey}
        auctionStatuses={AuctionStatuses}
      />

      <Portlet className="pt0" bodyClassName="form-horizontal">
        <div className="row">
          <div className="col-2">
            <span>
              {I.t('AUCTIONS_CHOSEN')}: <strong>{selectedRowsTotal}</strong>
            </span>
          </div>
          {selectedRowsAuctions && selectedRowsAuctions.length > 0 ? (
            <div className="col-4">
              <Popconfirm
                placement="bottom"
                title={I.t('DELETE_SELECTED_CONFIRMATION_PL')}
                onConfirm={submitCancelAuctions}
                okText="Yes"
                cancelText="No"
              >
                <Button type="danger" size="small">
                  {I.t('CANCEL')}
                </Button>
              </Popconfirm>
            </div>
          ) : (
            <div className="col-4" />
          )}
          <div className="col-6">
            {isLast ? null : (
              <Button className="pull-right" type="link" onClick={loadOlder}>
                {stepTitleOlder}
              </Button>
            )}
            {currentStep > 0 ? (
              <Button className="pull-right" type="link" onClick={loadNewer}>
                {stepTitleNewer}
              </Button>
            ) : null}
          </div>
        </div>

        <div>
          <Spin spinning={fetchingAuctions} size="large" tip="Loading...">
            {hasOnline && (
              <Table
                className="shadow"
                pagination={false}
                rowKey={x => x.ID}
                rowSelection={isOperator ? rowSelection : null}
                columns={columnDefs}
                dataSource={onlineFilteredAuctions}
                rowClassName={() => 'table-row-height'}
                scroll={{
                  x: columnsWidth,
                }}
              />
            )}
            {(normalFilteredAuctions.length > 0 || !hasOnline) && (
              <Table
                className="shadow"
                showHeader={!hasOnline}
                pagination={pagination}
                rowKey={x => x.ID}
                rowSelection={isOperator ? rowSelection : null}
                columns={columnDefs}
                dataSource={normalFilteredAuctions}
                rowClassName={() => 'table-row-height'}
                scroll={{
                  x: columnsWidth,
                }}
              />
            )}
          </Spin>
        </div>
      </Portlet>
      {isMerchant &&
        (user.MembershipId === getMembershipId(Memberships.Bronze) &&
        goldMarketValue ? (
          <UpgradeMembershipModal
            show={showUpgradeModal}
            onHide={() => setShowUpgradeModal(false)}
          />
        ) : (
          <AuctionBidModal
            isBronze={
              user.MembershipId === getMembershipId(Memberships.Bronze) &&
              goldMarketValue
            }
            show={modalType === 'offer-auction'}
            onHide={hideModals}
            loading={submitting}
            onSubmit={onBidSubmit}
            auction={selectedAuction}
            bidStep={bidStep}
            best={best}
            current={current}
            userId={user.PartyId}
            creatingAuctionBidsFor={creatingAuctionBidsFor}
            merchantFees={merchantFees}
            membershipID={user.MembershipId}
          />
        ))}

      {can.cancel && (
        <ConfirmationModal
          onHide={() => setAuctionToCancel({})}
          loading={submitting}
          show={Object.keys(auctionToCancel).length > 0}
          title={I.t('AUCTION_CANCEL')}
          onSubmit={() => cancelAuction(auctionToCancel.ID)}
          rejectButton={false}
          promptText={
            <div className="note note-danger">
              {I.t('AUCTION_CANCEL_FOR')}
              <span className="bold" /> {I.t('MARKET_VALUE')}{' '}
              <span className="bold danger">
                {formatCurrencyRounded(auctionToCancel.MarketValue)}
              </span>
              <br />
              <br />
              {I.t('ARE_YOU_SURE')}
            </div>
          }
        />
      )}

      {can.cancelAuction ? (
        <ConfirmationModal
          onHide={e => setModalType(null)}
          loading={cancelling}
          show={modalType === 'cancel-auction'}
          title={I.t('AUCTION_CANCEL_ALL')}
          onSubmit={submitCancelAuctions}
          rejectButton={false}
          promptText={
            <div className="note note-danger">
              {I.t('AUCTION_CANCEL_ALL_ASK')}
              <br />
              <br />
              {I.t('ARE_YOU_SURE')}
            </div>
          }
        />
      ) : (
        false
      )}
      {can.endAuction ? (
        <ConfirmationModal
          onHide={e => setModalType(null)}
          loading={submitting}
          show={modalType === 'end-auctions'}
          title={I.t('AUCTION_SELECT_LOWEST_PRICES_ALL')}
          onSubmit={submitEndAuctions}
          rejectButton={false}
          promptText={
            <div className="note note-danger">
              {I.t('AUCTION_SELECT_BEST_PRICES_ALL_ASK')}
              <br />
              <br />
              {I.t('ARE_YOU_SURE')}
            </div>
          }
        />
      ) : (
        false
      )}
      {
        // datepicker modal for starting an auction
        auctionStatus === AuctionStatuses.Pending && isOperator && (
          <AuctionDurationModal
            show={!!auctionToStartId}
            submitButton={I.t('APPROVAL')}
            loading={submitting}
            onHide={e => setAuctionToStartId(null)}
            onSubmit={endDate =>
              startAuctions([{ ID: auctionToStartId, EndDate: endDate }])
            }
          />
        )
      }
      {
        // datepicker modal for starting selected auctions
        can.startAuction && (
          <AuctionDurationModal
            show={modalType === 'auction-duration'}
            submitButton={I.t('APPROVAL')}
            loading={submitting}
            onSubmit={submitStartAuctions}
            onHide={e => setModalType(null)}
          />
        )
      }
      {auctionStatus === AuctionStatuses.ForAcceptance && isMerchant && (
        <ConfirmationModal
          onHide={() => setAuctionToAccept({})}
          loading={submitting}
          show={Object.keys(auctionToAccept).length > 0}
          title={I.t('OFFER_APPROVE')}
          onSubmit={() => acceptAuction(auctionToAccept.ID)}
          withComments={false}
          rejectButton={false}
          //onReject={ (comments) => rejectAuction(auctionToAccept.ID, comments) }
          promptText={I.t('OFFER_APPROVE_ASK')}
        />
      )}

      {(auctionStatus === AuctionStatuses.ForDispatch ||
        auctionStatus === AuctionStatuses.DispatchProblem) &&
      isMerchant ? (
        <ConfirmationModal
          onHide={() => setAuctionToDispatch({})}
          loading={submitting}
          show={Object.keys(auctionToDispatch).length > 0}
          title={I.t('PART_SEND')}
          onSubmit={() => dispatchAuction(auctionToDispatch.ID)}
          withComments={true}
          rejectButton={false}
          //onReject={ (comments) => dispatchAuction(auctionToDispatch.ID, true, comments) }
          promptText={I.t('PART_SEND_FINISHED_ASK')}
        />
      ) : (
        false
      )}

      {galleryImages && (
        <ImageGallery
          imageGalleryRef={imageGalleryRef}
          items={galleryImages}
          onScreenChange={fullScreenElem =>
            !fullScreenElem && setGalleryImages(null)
          }
        />
      )}
    </div>
  ) : null;
};

AuctionsList.contextTypes = {
  pageNo: PropTypes.number,
  setGridPage: PropTypes.string,
};

export default AuctionsList;
